import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Row, Col, Select, InputNumber, Slider, Progress, Tabs } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './BikeSpeedCalculator.css';
import { request } from '../../utils/request'; // 导入我们的request函数
import RouteAnalysis from './RouteAnalysis'; // 导入路段分析组件

const { TabPane } = Tabs;
const { Option } = Select;

const BikeSpeedCalculator = () => {
  // 状态管理
  const [form] = Form.useForm();
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('1');
  const [bikeTypes, setBikeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);
  
  // 添加骑行计划相关状态
  const [ridePlanForm] = Form.useForm();
  const [calculatingRidePlan, setCalculatingRidePlan] = useState(false);
  const [ridePlanResult, setRidePlanResult] = useState(null);

  // 默认参数值（初始值，稍后会从API获取）
  const [defaultValues, setDefaultValues] = useState({
    bike_type: 'racing_drops',
    power: 160,
    cadence: 85,
    rider_weight: 75,
    rider_height: 175,
    bike_weight: 8,
    humidity: 50,
    slope: 0,
    wind_speed: 0,
    temperature: 20,
    altitude: 0,
  });

  // 在组件挂载时获取自行车类型和默认参数
  useEffect(() => {
    Promise.all([
      fetchBikeTypes(),
      fetchDefaultParams()
    ]).then(() => {
      setLoading(false);
    }).catch(error => {
      console.error('初始化数据获取失败:', error);
      setLoading(false);
    });
  }, []);

  // 获取自行车类型列表
  const fetchBikeTypes = async () => {
    try {
      const data = await request({
        url: 'bike/bike-types',
        method: 'GET'
      });
      
      if (data && data.success && Array.isArray(data.data)) {
        const options = data.data.map(type => ({
          value: type.id,
          label: type.name
        }));
        
        setBikeTypes(options);
        
        // 如果有默认值，设置它
        if (options.length > 0) {
          form.setFieldsValue({ bike_type: options[0].value });
        }
      }
    } catch (error) {
      console.error('获取自行车类型出错:', error);
    }
  };

  // 获取默认参数
  const fetchDefaultParams = async () => {
    try {
      const data = await request({
        url: 'bike/default-params',
        method: 'GET'
      });
      
      if (data && data.success && data.data) {
        const defaults = data.data;
        
        form.setFieldsValue({
          rider_weight: defaults.riderWeight?.toString() || '75',
          rider_height: defaults.riderHeight?.toString() || '175',
          bike_weight: defaults.bikeWeight?.toString() || '8',
          slope: defaults.slope?.toString() || '0',
          temperature: defaults.temperature?.toString() || '20',
          altitude: defaults.altitude?.toString() || '0',
          humidity: defaults.humidity?.toString() || '50',
          wind_speed: defaults.windSpeed ? (-defaults.windSpeed).toString() : '0', // 注意这里取反
          cadence: defaults.cadence?.toString() || '90',
          power: defaults.power?.toString() || '200'
        });
      }
    } catch (error) {
      console.error('获取默认参数出错:', error);
    }
  };

  // 计算速度的函数
  const calculateSpeed = async (values = null) => {
    setCalculating(true);
    try {
      if (!values) {
        values = form.getFieldsValue();
      }
      
      // 使用request函数替代直接fetch
      console.log('发送计算请求到: bike/calculate');
      
      const requestBody = {
        bikeType: values.bike_type,
        riderWeight: parseFloat(values.rider_weight),
        riderHeight: parseFloat(values.rider_height),
        bikeWeight: parseFloat(values.bike_weight),
        slope: parseFloat(values.slope),
        temperature: parseFloat(values.temperature),
        altitude: parseFloat(values.altitude),
        humidity: parseFloat(values.humidity),
        windSpeed: -parseFloat(values.wind_speed), // UI中正值表示顺风，API中正值表示逆风，需要取反
        cadence: parseFloat(values.cadence || 90),
        power: parseFloat(values.power)
      };
      
      console.log('请求参数:', requestBody);
      
      const data = await request({
        url: 'bike/calculate',
        method: 'POST',
        data: requestBody
      });
      
      console.log('API返回数据:', data);
      
      // 验证数据存在
      if (!data || !data.success || data.error) {
        console.error('API返回无效数据:', data);
        throw new Error(data?.error || '计算服务返回无效数据');
      }
      
      // 格式化结果以适应UI组件的期望
      setResult({
        speed: data.data.speed ? data.data.speed.toFixed(1) : '0.0',
        air_resistance: data.data.airResistancePower ? data.data.airResistancePower.toFixed(1) : '0.0',
        rolling_resistance: data.data.rollingResistancePower ? data.data.rollingResistancePower.toFixed(1) : '0.0',
        slope_force: data.data.slopePower ? data.data.slopePower.toFixed(1) : '0.0',
        cda: data.data.cda ? data.data.cda.toFixed(4) : '0.0000',
        success: true
      });
    } catch (error) {
      console.error('请求错误:', error);
      setResult({
        speed: '0.000',
        air_resistance: '0.000',
        rolling_resistance: '0.000',
        slope_force: '0.000',
        cda: '0.0000',
        success: false,
        error: error.message
      });
    } finally {
      setCalculating(false);
    }
  };

  // 表单提交处理
  const handleSubmit = (values) => {
    calculateSpeed(values);
  };

  // 计算骑行计划
  const calculateRidePlan = async () => {
    try {
      setCalculatingRidePlan(true);
      
      // 获取骑行计划表单值
      const planValues = ridePlanForm.getFieldsValue();
      const distance = parseFloat(planValues.distance);
      const elevation = parseFloat(planValues.elevation);
      
      // 计算平均坡度 (保留一位小数)
      // 坡度 = 高度变化/距离 * 100%
      // 距离需要从km转为m，所以是: 爬升(m)/(距离(km)*1000)*100
      const averageSlope = (elevation / (distance * 1000) * 100).toFixed(1);
      
      // 设置主表单的坡度值
      form.setFieldsValue({ slope: averageSlope });
      
      // 获取其他必要的值并计算速度
      const values = form.getFieldsValue();
      values.slope = averageSlope; // 确保使用新计算的坡度值
      
      // 直接计算速度并获取结果，而不是依赖于result状态
      setCalculating(true);
      try {
        const requestBody = {
          bikeType: values.bike_type,
          riderWeight: parseFloat(values.rider_weight),
          riderHeight: parseFloat(values.rider_height),
          bikeWeight: parseFloat(values.bike_weight),
          slope: parseFloat(values.slope),
          temperature: parseFloat(values.temperature),
          altitude: parseFloat(values.altitude),
          humidity: parseFloat(values.humidity),
          windSpeed: -parseFloat(values.wind_speed), // UI中正值表示顺风，API中正值表示逆风，需要取反
          cadence: parseFloat(values.cadence || 90),
          power: parseFloat(values.power)
        };
        
        const data = await request({
          url: 'bike/calculate',
          method: 'POST',
          data: requestBody
        });
        
        if (!data || !data.success || data.error) {
          console.error('API返回无效数据:', data);
          throw new Error(data?.error || '计算服务返回无效数据');
        }
        
        // 更新结果状态
        const speedResult = {
          speed: data.data.speed ? data.data.speed.toFixed(1) : '0.0',
          air_resistance: data.data.airResistancePower ? data.data.airResistancePower.toFixed(1) : '0.0',
          rolling_resistance: data.data.rollingResistancePower ? data.data.rollingResistancePower.toFixed(1) : '0.0',
          slope_force: data.data.slopePower ? data.data.slopePower.toFixed(1) : '0.0',
          cda: data.data.cda ? data.data.cda.toFixed(4) : '0.0000',
          success: true
        };
        
        setResult(speedResult);
        
        // 计算骑行时间 (小时)
        // 时间 = 距离/速度
        const speed = parseFloat(speedResult.speed);
        const estimatedTime = speed > 0 ? distance / speed : 0;
        
        // 转换为小时和分钟格式
        const hours = Math.floor(estimatedTime);
        const minutes = Math.round((estimatedTime - hours) * 60);
        
        // 估算能量消耗 (kJ)
        // 能量 = 功率 * 时间(小时) * 3600 / 1000
        const power = parseFloat(values.power);
        const energyConsumption = (power * estimatedTime * 3.6).toFixed(0);
        
        // 保存结果
        setRidePlanResult({
          time: `${hours}小时 ${minutes}分钟`,
          averageSlope: averageSlope,
          energy: energyConsumption
        });
      } finally {
        setCalculating(false);
      }
    } catch (error) {
      console.error('骑行计划计算错误:', error);
    } finally {
      setCalculatingRidePlan(false);
    }
  };

  // 绘制结果指示
  const renderResultIndicator = () => {
    if (!result) return null;
    
    const totalPower = result.power || form.getFieldValue('power') || defaultValues.power;
    const airPercentage = Math.abs((result.air_resistance / totalPower) * 100);
    const rollingPercentage = Math.abs((result.rolling_resistance / totalPower) * 100);
    const slopePercentage = Math.abs((result.slope_force / totalPower) * 100);
    
    return (
      <div className="result-section">
        <div className="speed-result">
          <span className="speed-value">{result.speed}</span>
          <span className="speed-unit">km/h</span>
        </div>
        
        <div className="resistance-details">
          <div className="resistance-item">
            <div className="resistance-label">CdA: {result.cda} m²</div>
          </div>
          
          <div className="resistance-item">
            <div className="resistance-label">空气阻力: {result.air_resistance} W</div>
            <Progress 
              percent={airPercentage} 
              status="active" 
              strokeColor="#1890ff" 
              showInfo={false}
            />
          </div>
          
          <div className="resistance-item">
            <div className="resistance-label">滚动阻力: {result.rolling_resistance} W</div>
            <Progress 
              percent={rollingPercentage} 
              status="active" 
              strokeColor="#52c41a" 
              showInfo={false}
            />
          </div>
          
          <div className="resistance-item">
            <div className="resistance-label">坡度阻力: {result.slope_force} W</div>
            <Progress 
              percent={slopePercentage} 
              status="active" 
              strokeColor="#faad14" 
              showInfo={false}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bike-calculator-container">
      <h1 className="page-title">骑行工具箱</h1>
      <p className="page-description">
        骑行功率速度计算与路线规划分析工具
      </p>
      
      {loading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div className="loading-spinner"></div>
            <p>正在加载数据...</p>
          </div>
        </Card>
      ) : (
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="速度计算" key="1">
            <Row gutter={16}>
              <Col md={12}>
                <Card title="参数设置" bordered={false}>
                  <Form
                    form={form}
                    layout="vertical"
                    initialValues={defaultValues}
                    onFinish={handleSubmit}
                  >
                    <Form.Item label="自行车类型" name="bike_type">
                      <Select>
                        {bikeTypes.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="功率 (W)" name="power">
                          <InputNumber min={50} max={1000} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="踏频 (rpm)" name="cadence">
                          <InputNumber min={40} max={150} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="骑行者体重 (kg)" name="rider_weight">
                          <InputNumber min={30} max={150} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="骑行者身高 (cm)" name="rider_height">
                          <InputNumber min={140} max={220} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="自行车重量 (kg)" name="bike_weight">
                          <InputNumber min={5} max={30} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="相对湿度 (%)" name="humidity">
                          <InputNumber min={0} max={100} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="坡度 (%)" name="slope">
                          <InputNumber min={-10} max={20} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="风速 (km/h)" name="wind_speed" tooltip="正值=顺风，负值=逆风">
                          <InputNumber min={-40} max={40} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="温度 (°C)" name="temperature">
                          <InputNumber min={-20} max={50} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="海拔 (m)" name="altitude">
                          <InputNumber min={0} max={5000} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Form.Item>
                      <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={calculating}
                        block
                      >
                        计算
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              </Col>
              
              <Col md={12}>
                <Card 
                  title="计算结果" 
                  bordered={false}
                  bodyStyle={{ padding: '12px 16px' }}
                >
                  {result ? renderResultIndicator() : (
                    <div className="empty-result">
                      <p>调整参数并点击"计算"按钮查看结果</p>
                    </div>
                  )}
                  
                  {/* 添加骑行计划到计算结果卡片下方 */}
                  <div className="ride-plan-section">
                    <h3 className="ride-plan-title">骑行计划</h3>
                    <Form 
                      layout="vertical" 
                      form={ridePlanForm}
                      initialValues={{ 
                        distance: 100, 
                        elevation: 1000 
                      }}
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item 
                            label="骑行距离 (km)" 
                            name="distance"
                            rules={[{ required: true, message: '请输入骑行距离' }]}
                          >
                            <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item 
                            label="爬升 (m)" 
                            name="elevation"
                            rules={[{ required: true, message: '请输入爬升高度' }]}
                          >
                            <InputNumber min={0} max={10000} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label=" ">
                            <Button 
                              type="primary" 
                              block 
                              onClick={calculateRidePlan} 
                              loading={calculatingRidePlan}
                            >
                              计算骑行时间
                            </Button>
                          </Form.Item>
                        </Col>
                      </Row>
                      
                      {ridePlanResult && (
                        <div className="ride-plan-result">
                          <div className="result-item">
                            <span className="result-label">预计需要时间:</span>
                            <span className="result-value">{ridePlanResult.time}</span>
                          </div>
                          <div className="result-item">
                            <span className="result-label">平均坡度:</span>
                            <span className="result-value">{ridePlanResult.averageSlope}%</span>
                          </div>
                          <div className="result-item">
                            <span className="result-label">能量消耗:</span>
                            <span className="result-value">{ridePlanResult.energy} kJ</span>
                          </div>
                        </div>
                      )}
                    </Form>
                  </div>
                </Card>
              </Col>
            </Row>
          </TabPane>
          
          <TabPane tab="路段分析" key="2">
            <RouteAnalysis />
          </TabPane>
        </Tabs>
      )}
    </div>
  );
};

export default BikeSpeedCalculator; 